import { useContext, useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { MdContentCopy, MdToggleOn, MdToggleOff, MdCheck } from "react-icons/md";
import { 
  Modal, 
  Box, 
  Typography, 
  Button, 
  IconButton, 
  CircularProgress,
  Divider
} from "@mui/material";
import { BiX } from "react-icons/bi";

import { HeaderComponent } from "../../../../components/components/js/HeaderComponent";
import { TableComponent } from "../../../../components/components/js/TableComponent";
import { LoadingDots } from "../../../../components/general";

import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import AuthContext from "../../../../context/AuthProvider";
import "../../../../components/components/css/RoomModal.css"; 
import API_URLS from "../../../../constants/apiConstants";

const COLUMNS = ["link", "expiry", "activation", "apps", "status", "actions"];

export const AdmissionFormLink = () => {
  const { t, i18n } = useTranslation();
  const axiosPrivate = useAxiosPrivate(); 
  const { school } = useContext(AuthContext); 
  
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({ expiration_date: "", days_valid: "" });
  const [errors, setErrors] = useState({});

  const isAr = i18n.language === 'ar';
  const baseUrl = window.location.origin;
  //const baseUrl=process.env.REACT_APP_BASE_URL;

  // Calculate tomorrow's date
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  const fetchLinks = useCallback(async () => {
    if (!school?.id) return;
    setLoading(true);
    try {
      const response = await axiosPrivate.get(API_URLS.getAdmissionLinks(school.id));
      setLinks(response.data);
    } catch (error) {
      toast.error(isAr ? "فشل في جلب روابط التقديم" : "Failed to fetch admission links");
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, school?.id, t]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const validateForm = () => {
    const newErrors = {};
    if (formData.days_valid && (parseInt(formData.days_valid) <= 0)) {
      newErrors.days_valid = [isAr ? "يجب أن يكون الرقم أكبر من 0" : "Must be greater than 0"];
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateLink = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});
    try {
      const payload = { nursery: school.id  , title: formData.title};
      if (formData.expiration_date) payload.expiration_date = formData.expiration_date;
      else if (formData.days_valid) payload.days_valid = parseInt(formData.days_valid);

      await axiosPrivate.post(API_URLS.generateAdmissionLink(), payload);
      toast.success(isAr ? "تم إنشاء الرابط بنجاح" : "Link created successfully");
      handleCloseModal();
      fetchLinks();
    } catch (error) {
      if (error.response && error.response.status === 400) {
        setErrors(error.response.data); 
      } else {
        toast.error(isAr ? "فشل إنشاء الرابط" : "Failed to create link");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setFormData({title: "", expiration_date: "", days_valid: "" });
    setErrors({});
  };

  const handleToggleStatus = async (id, isCurrentActive) => {
    const action = isCurrentActive ? 'deactivate' : 'reactivate';
    try {
      await axiosPrivate.post(API_URLS.toggleLinkStatus(id, action)); 
      fetchLinks(); 
    } catch (error) { toast.error(isAr ? "فشل في تحديث الحالة" : "Update failed"); }
  };

  const handleDelete = async (row) => {
    try {
      setLoading(true);
      await axiosPrivate.delete(API_URLS.deleteAdmissionLink(row.id));
      fetchLinks();
    } catch (err) { toast.error(isAr ? "فشل في حذف الرابط" : "Delete error"); }
    finally { setLoading(false); }
  };

  return (
    <div className="px-4" style={{ minHeight: "70vh", marginLeft: "auto" }}>
      <HeaderComponent
        title="admission_form"
        buttonName="create_link"
        setShow={() => setShowAddModal(true)} 
      />

      {loading ? <LoadingDots /> : (
        <TableComponent
          columns={COLUMNS}
          page="admission"
          onDelete={handleDelete}         
          data={links.map((link) => ({
            ...link,
            link: (
              <div 
                className="d-flex align-items-center gap-2" 
                style={{ 
                  justifyContent: "flex-start",
                  width: "fit-content",
                  direction: "ltr"
                }}
              >
                <button 
                    className="btn btn-sm btn-link p-0 border-0 shadow-none" 
                    onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(`${baseUrl}/admission/${link.token}`); setCopiedId(link.id); setTimeout(() => setCopiedId(null), 2000); }}
                    style={{ order: isAr ? 2 : 1 }}
                >
                  {copiedId === link.id ? <MdCheck size={18} color="#28a745" /> : <MdContentCopy size={18} color="#728292ff" />}
                </button>
                <a 
                  href={`${baseUrl}/admission/${link.token}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary text-decoration-none hover-link"
                  style={{ 
                    order: isAr ? 1 : 2, 
                    margin: 0, 
                    fontSize: '0.875rem', 
                    cursor: 'pointer' ,
                    color: '#1976d2'
                  }}
                  onClick={(e) => e.stopPropagation()} 
                >
                  {link.title}
                </a>
              </div>
            ),
            expiry: link.expiration_date ? new Date(link.expiration_date).toLocaleDateString() : "--",
            activation: (
              <div style={{ cursor: link.expiration_date ? "default" : "pointer", fontSize: "28px" }} onClick={() => !link.expiration_date && handleToggleStatus(link.id, link.is_active)}>
                {link.expiration_date ? <span className="badge bg-light text-dark border" style={{fontSize: '12px'}}>{isAr ? "تلقائي" : "Auto"}</span> : (link.is_active ? <MdToggleOn color="#28a745" size={40} /> : <MdToggleOff color="#ccc"  size={40}/>)}
              </div>
            ),
            apps: link.applications_count || 0,
          status: (
            <span 
              style={{
                padding: "4px 12px",
                borderRadius: "12px",
                fontSize: "11px",
                fontWeight: "600",
                display: "inline-block",
                textAlign: "center",
                minWidth: "75px",
                backgroundColor: 
                  link.status === 'active' ? "#e8f5e9" : 
                  link.status === 'expired' ? "#ffebee" : "#f5f5f5",
                color: 
                  link.status === 'active' ? "#2e7d32" : 
                  link.status === 'expired' ? "#c62828" : "#616161",
              }}
            >
              {link.status === 'active' 
                ? (isAr ? "نشط" : "Active") 
                : link.status === 'expired' 
                  ? (isAr ? "منتهي" : "Expired") 
                  : (isAr ? "غير نشط" : "Inactive")
              }
            </span>
          ),
          }))}
        />
      )}


      {/* Create Link Model */}
      <Modal open={showAddModal} onClose={handleCloseModal}>
        <Box className="modalStyle" dir={isAr ? "rtl" : "ltr"}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <Typography variant="h5" fontWeight="bold" color="secondary">{isAr ? "إنشاء رابط تقديم جديد" : "Create New Admission Link"}</Typography>
            <IconButton onClick={handleCloseModal} size="small"><BiX /></IconButton>
          </Box>

          {/* Link Title Field */}
          <Box mb={3}>
            <Typography variant="body1" color="secondary" gutterBottom>
              {isAr ? "عنوان الرابط" : "Link Title"} <span style={{color: 'red'}}>*</span>
            </Typography>
            <input
              type="text"
              className={`form-control ${errors.title ? "is-invalid" : ""}`}
              placeholder={isAr ? "مثلاً: تقديمات يناير 2026" : "e.g. January 2026 Admission"}
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                if (errors.title) setErrors({ ...errors, title: null });
              }}
              disabled={isSubmitting}
            />
            {errors.title && (
              <Typography className="errorText" style={{color: '#d32f2f', fontSize: '12px', marginTop: '4px'}}>
                {errors.title[0]}
              </Typography>
            )}
          </Box>

          <Box mb={3}>
            <Typography variant="body1" color="secondary" gutterBottom>{isAr ? "تاريخ الانتهاء" : "Expiration Date"}</Typography>
            <input
              type="date"
              className={`form-control ${errors.expiration_date ? "is-invalid" : ""}`}
              min={minDate}
              value={formData.expiration_date}
              onChange={(e) => { setFormData({ ...formData, expiration_date: e.target.value, days_valid: "" }); setErrors({}); }}
              disabled={formData.days_valid !== "" || isSubmitting}
            />
            {errors.expiration_date && <Typography className="errorText" style={{color: '#d32f2f', fontSize: '12px', marginTop: '4px'}}>{errors.expiration_date[0]}</Typography>}
          </Box>

          <Box my={3} display="flex" alignItems="center" gap={2}><Divider sx={{ flex: 1 }} /><Typography variant="body2" color="textSecondary" fontWeight="bold">{isAr ? "أو" : "OR"}</Typography><Divider sx={{ flex: 1 }} /></Box>

          <Box mb={3}>
            <Typography variant="body1" color="secondary" gutterBottom>{isAr ? "صالح لمدة (أيام)" : "Valid for (Days)"}</Typography>
            <input
              type="number"
              className={`form-control ${errors.days_valid ? "is-invalid" : ""}`}
              placeholder={isAr ? "مثلاً: 10" : "e.g. 10"}
              value={formData.days_valid}
              onChange={(e) => { setFormData({ ...formData, days_valid: e.target.value, expiration_date: "" }); setErrors({}); }}
              disabled={formData.expiration_date !== "" || isSubmitting}
            />
            {errors.days_valid && <Typography className="errorText" style={{color: '#d32f2f', fontSize: '12px', marginTop: '4px'}}>{errors.days_valid[0]}</Typography>}
          </Box>

          {/* Note*/}
          <Typography variant="body2" color="secondary" mb={4}>
            <strong>{isAr ? "ملاحظة" : "Note"}:</strong> {isAr ? "اترك الحقلين فارغين لإنشاء رابط دائم (يتم إغلاقه يدوياً)." : "Leave both fields empty for a permanent link (manual toggle)."}
          </Typography>

          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" color="primary" onClick={handleCreateLink} disabled={isSubmitting} className="submitButton" startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}>
              {isSubmitting ? (isAr ? "جاري الإنشاء..." : "Creating...") : (isAr ? "إنشاء الرابط" : "Create Link")}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};